import React, { ReactElement, useContext, useEffect, useLayoutEffect, useState } from 'react'
import './css/config.css'
import './css/darkmode.css'
import './css/tabs.css'
import './css/icons.css'
import './css/main.css'
import './css/graphs.css'
import './css/navbar.css'
import './css/settings.css'
import './css/snippets.css'
import './css/thumbnails.css'
import './css/write.css'
import './css/search.css'
import './css/responsive.css'
import NavBar from './NavBar/NavBar'
import { About } from './site/About'
import EntryPage from './Entry/EntryPage'
import PersonPage from './Person/PersonPage'
import { Routes, Route } from 'react-router-dom'
import ErrorMessage from './common/ErrorMessage'
import ErrorBoundary from './site/ErrorBoundary'
import Login, { isLoggedIn } from './site/Login'
import { AppContext } from './contexts/AppContext'
import SettingsPage from './Settings/SettingsPage'
import PersonContextProvider from './contexts/PersonContext'
import EntryContextProvider from './contexts/EntryContext'
import SearchPage from './Search/SearchPage'
import EntrySpans from './Entry/EntrySpans'
import ExplorePage from './Explore/ExplorePage'

/** Detect the Opera browser for the sake of some CSS hacks */
const isOpera = 'opr' in window || 'opera' in window || navigator.userAgent.indexOf(' OPR/') >= 0

const LoggedInApp = (): ReactElement => {
    const { isUserType } = useContext(AppContext)
    return <div id="trove" className={ (isUserType('power') ? 'power-user' : 'standard-user') + ' ' + (isOpera ? 'opera' : '') }>
        <div className="app">
            <ErrorBoundary>
                <EntryContextProvider>
                    <PersonContextProvider>
                        <Routes>
                            <Route path="/about/:page" element={ <About /> } />
                            <Route path="/about" element={ <About /> } />
                            <Route path="/settings/*" element={ <SettingsPage /> } />
                            <Route path="/admin/*" element={ <SettingsPage /> } />
                            <Route path="/redirect/*" element={ <SettingsPage /> } />
                            <Route path="/entry/*" element={ <EntryPage /> } />
                            <Route path="/entries/*" element={ <EntryPage /> } />
                            <Route path="/write/*" element={ <EntryPage /> } />
                            <Route path="/today/*" element={ <EntryPage /> } />
                            <Route path="/person/*" element={ <PersonPage /> } />
                            <Route path="/people/*" element={ <PersonPage /> } />
                            <Route path="/explore/*" element={ <ExplorePage /> } />
                            <Route path="/search/*" element={ <SearchPage /> } />
                            <Route path="/spans/*" element={ <EntrySpans /> } />
                            <Route path="/" element={ <EntryPage /> } />
                            <Route
                                path="*"
                                element={
                                    <ErrorMessage status={ 404 } error={ { message: 'Oh no! That path leads nowhere!' } } />
                                } />
                        </Routes>
                    </PersonContextProvider>
                </EntryContextProvider>
            </ErrorBoundary>
        </div>
        <NavBar />
    </div>
}

const App = (): ReactElement => {
    const { appState } = useContext(AppContext)
    const [darkModeSetting, setDarkModeSetting] = useState(() => localStorage.getItem('dark_mode') ?? 'system')
    const [systemDark, setSystemDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches)
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)')
        const mqHandler = (e: MediaQueryListEvent): void => setSystemDark(e.matches)
        const storageHandler = (e: StorageEvent): void => {
            if (e.key === 'dark_mode') setDarkModeSetting(e.newValue ?? 'system')
        }
        mq.addEventListener('change', mqHandler)
        window.addEventListener('storage', storageHandler)
        return () => {
            mq.removeEventListener('change', mqHandler)
            window.removeEventListener('storage', storageHandler)
        }
    }, [])
    const isDark = darkModeSetting === 'dark'
      || (darkModeSetting !== 'light' && systemDark)
    useLayoutEffect(() => {
        document.body.classList.toggle('dark', isDark)
    }, [isDark])
    return isLoggedIn(appState)
        ? <LoggedInApp />
        : <Routes>
            <Route path="/about/:page" element={ <About /> } />
            <Route path="/about" element={ <About /> } />
            <Route path="*" element={ <Login /> } />
        </Routes>
}

export default App
